import type { ILogger } from "../../../../../../shared/logging/logger";
import type InlineQuestionResponse from "../types/InlineQuestionResponse";
import type { QualificationAttribute } from "../types/JobAttribute";

import Sentiment from "../../../../../../shared/util/salary/Sentiment";
import { InferenceMethod } from "../requirements/TaxonomyEntity";

import {
  AvroOptional,
  type JmProviderRequestRN,
  log as logJmProviderRequest,
} from "@indeed/signals-schema/com/indeed/signals/schema/profile/JmProviderRequestRN";

export enum LogEventType {
  profileRedirectClick = "profileRedirectClick",
  trafficLightAttributesShown = "trafficLightAttributesShown",
  jobseekerDataCollectionShown = "jobseekerDataCollectionShown",
  qualificationQuestionRendered = "qualificationQuestionRendered",
  qualificationsQuestionsSkip = "qualificationsQuestionsSkip",
  qualificationsQuestionsReset = "qualificationsQuestionsReset",
  qualificationsQuestionsClick = "qualificationsQuestionsClick",
  editableQualificationsModalOpen = "editableQualificationsModalOpen",
  editableQualificationsModalClose = "editableQualificationsModalClose",
  attributeListExpanded = "attributeListExpanded",
  logReceivedFlagTypeFailed = "logReceivedFlagTypeFailed",
  profileInsightsShown = "profileInsightsShown",
  gatedMatchOverviewShown = "gatedMatchOverviewShown",
}

export default class FrontendLogger {
  public static readonly TAB_CLICK = "tabClick";

  private readonly iLogger: ILogger;
  private readonly providerId: string;
  private accountKey?: string;
  private clientTk?: string; // temp addition to diagnose JMREQ-847

  constructor(iLogger: ILogger, providerId: string) {
    this.iLogger = iLogger;
    this.providerId = providerId;
  }

  public setAccountKey(accountKey?: string) {
    this.accountKey = accountKey;
  }

  public getAccountKey(): string {
    return this.accountKey ?? "";
  }

  public setClientTk(clientTk: string) {
    this.clientTk = clientTk;
  }

  public getClientTk(): string {
    return this.clientTk ?? "";
  }

  public logProviderRequest(
    message: Omit<
      JmProviderRequestRN,
      | "accountKey"
      | "clientTk"
      | "clientApp"
      | "clientContext"
      | "mobile"
      | "deducedAttributes"
      | "displayableExtractedJobseekerQualsSuids"
      | "displayableExtractedJobseekerQualsSuidCount"
      | "displayableDeducedJobseekerQualsSuids"
      | "displayableDeducedJobseekerQualsSuidCount"
    >,
    attributes?: QualificationAttribute[],
  ): void {
    let deducedAttributes: Partial<ReturnType<typeof this.getDeducedAndExtractedData>> = {};
    let displayableAttributes: ReturnType<typeof this.getQualificationData> = {};
    if (attributes && attributes?.length > 0) {
      deducedAttributes = this.getDeducedAndExtractedData(attributes);
      displayableAttributes = this.getQualificationData(attributes);
    }
    logJmProviderRequest({
      accountKey: this.getAccountKey(),
      clientTk: this.getClientTk(),
      clientApp: "mobile",
      clientContext: "rn-viewjob",
      mobile: true,
      ...deducedAttributes,
      ...displayableAttributes,
      ...message,
    });
  }

  public logAction(data: { eventType: LogEventType; [key: string]: any }): void {
    this.iLogger.event?.trackEvent("jsuipAction", {
      logType: "jsuipAction",
      provider: this.providerId,
      data: { clientTk: this.getClientTk(), ...data },
    });
  }

  public logProfileNavClick(): void {
    this.logAction({
      eventType: LogEventType.profileRedirectClick,
    });
  }

  public logAttributeTilesShownForSection(
    attributesShown: QualificationAttribute[],
    answeredIds: string[],
    section: string,
  ) {
    const matched = attributesShown
      .filter((req) => req.sentiment === Sentiment.POSITIVE)
      .map((req) => req.uuid);
    const notMatched = attributesShown
      .filter((req) => req.sentiment === Sentiment.NEGATIVE)
      .map((req) => req.uuid);
    const unknownAttributes = attributesShown
      .filter((req) => !matched.includes(req.uuid) && !notMatched.includes(req.uuid))
      .map((req) => req.uuid);

    this.logAction({
      accountKey: this.getAccountKey(),
      eventType: LogEventType.trafficLightAttributesShown,
      section: section,
      answeredAttributes: answeredIds.join(","),
      matchedAttributes: matched.join(","),
      notMatchedAttributes: notMatched.join(","),
      unknownAttributes: unknownAttributes.join(","),
    });
  }

  public logDataCollectionShown(section?: string): void {
    this.logAction({
      accountKey: this.getAccountKey(),
      eventType: LogEventType.jobseekerDataCollectionShown,
      section: section ?? "",
    });
  }

  public logQualificationQuestionRendered(
    attibute: QualificationAttribute,
    ipCountry: string,
  ): void {
    this.logAction({
      ...this.getDefaultRequirementProperties(attibute),
      eventType: LogEventType.qualificationQuestionRendered,
      ipCountry,
    });
  }

  public logQualificationsQuestionsSkip(attibute: QualificationAttribute): void {
    this.logAction({
      ...this.getDefaultRequirementProperties(attibute),
      eventType: LogEventType.qualificationsQuestionsSkip,
    });
  }

  public logQualificationsQuestionsReset(
    attribute: QualificationAttribute,
    success: boolean,
  ): void {
    this.logAction({
      ...this.getDefaultRequirementProperties(attribute),
      eventType: LogEventType.qualificationsQuestionsReset,
      success,
      inferenceMethod: attribute.inferenceMethod,
    });
  }

  public logQualificationsQuestionButtonClick(
    sentiment: InlineQuestionResponse,
    attribute: QualificationAttribute,
    success: boolean,
    originalInferenceMethod: InferenceMethod,
  ): void {
    this.logAction({
      ...this.getDefaultRequirementProperties(attribute),
      eventType: LogEventType.qualificationsQuestionsClick,
      sentiment,
      success,
      inferenceMethod: originalInferenceMethod,
      originalSentiment: attribute.sentiment,
    });
  }

  public logEditableQualificationsModalOpen(attribute: QualificationAttribute): void {
    this.logAction({
      ...this.getDefaultRequirementProperties(attribute),
      eventType: LogEventType.editableQualificationsModalOpen,
    });
  }

  public logEditableQualificationsModalClose(attribute: QualificationAttribute): void {
    this.logAction({
      ...this.getDefaultRequirementProperties(attribute),
      eventType: LogEventType.editableQualificationsModalClose,
    });
  }

  public logListExpanded(newState: boolean, section: string) {
    this.logAction({
      eventType: LogEventType.attributeListExpanded,
      listExpanded: newState,
      section,
    });
  }

  public logReceivedFlagTypeFailed() {
    this.logAction({
      accountKey: this.getAccountKey(),
      eventType: LogEventType.logReceivedFlagTypeFailed,
    });
  }

  public logShowedProfileInsights() {
    this.logAction({
      accountKey: this.getAccountKey(),
      eventType: LogEventType.profileInsightsShown,
    });
  }

  public logShowedGatedMatchOverview() {
    this.logAction({
      accountKey: this.getAccountKey(),
      eventType: LogEventType.gatedMatchOverviewShown,
    });
  }

  public getInternalLogger() {
    return this.iLogger;
  }

  private getSuidsBySentiment(
    attributes: QualificationAttribute[],
    sentiment: Sentiment,
  ): string[] {
    return attributes.filter((req) => req.sentiment === sentiment).map((r) => r.suid);
  }

  private getQualificationData(
    attributes: QualificationAttribute[],
  ): Pick<
    JmProviderRequestRN,
    | "displayableJobQualificationSuids"
    | "displayableJobQualificationSuidCount"
    | "displayableMetJobQualificationSuids"
    | "displayableMetJobQualificationSuidCount"
    | "displayableUnmetJobQualificationSuids"
    | "displayableUnmetJobQualificationSuidCount"
    | "displayableUnknownJobQualificationSuids"
    | "displayableUnknownJobQualificationSuidCount"
    | "displayableSegmentJobQualificationSuids"
    | "displayableSegmentJobQualificationSuidCount"
    | "displayableMetSegmentJobQualificationSuids"
    | "displayableMetSegmentJobQualificationSuidCount"
    | "displayableUnmetSegmentJobQualificationSuids"
    | "displayableUnmetSegmentJobQualificationSuidCount"
    | "displayableUnknownSegmentJobQualificationSuids"
    | "displayableUnknownSegmentJobQualificationSuidCount"
  > {
    const displayableJobQualification = attributes.map((req) => req.suid);
    const displayableMetJobQualification = this.getSuidsBySentiment(attributes, Sentiment.POSITIVE);
    const displayableUnmetJobQualification = this.getSuidsBySentiment(
      attributes,
      Sentiment.NEGATIVE,
    );
    const displayableUnknownJobQualification = this.getSuidsBySentiment(
      attributes,
      Sentiment.UNKNOWN,
    );

    return {
      displayableJobQualificationSuids: AvroOptional.string(displayableJobQualification.join(",")),
      displayableJobQualificationSuidCount: AvroOptional.int(displayableJobQualification.length),
      displayableMetJobQualificationSuids: AvroOptional.string(
        displayableMetJobQualification.join(","),
      ),
      displayableMetJobQualificationSuidCount: AvroOptional.int(
        displayableMetJobQualification.length,
      ),
      displayableUnmetJobQualificationSuids: AvroOptional.string(
        displayableUnmetJobQualification.join(","),
      ),
      displayableUnmetJobQualificationSuidCount: AvroOptional.int(
        displayableUnmetJobQualification.length,
      ),
      displayableUnknownJobQualificationSuids: AvroOptional.string(
        displayableUnknownJobQualification.join(","),
      ),
      displayableUnknownJobQualificationSuidCount: AvroOptional.int(
        displayableUnknownJobQualification.length,
      ),
      displayableSegmentJobQualificationSuids: AvroOptional.string(""),
      displayableSegmentJobQualificationSuidCount: AvroOptional.int(0),
      displayableMetSegmentJobQualificationSuids: AvroOptional.string(""),
      displayableMetSegmentJobQualificationSuidCount: AvroOptional.int(0),
      displayableUnmetSegmentJobQualificationSuids: AvroOptional.string(""),
      displayableUnmetSegmentJobQualificationSuidCount: AvroOptional.int(0),
      displayableUnknownSegmentJobQualificationSuids: AvroOptional.string(""),
      displayableUnknownSegmentJobQualificationSuidCount: AvroOptional.int(0),
    };
  }

  private getDefaultRequirementProperties(attribute: QualificationAttribute) {
    return {
      accountKey: this.getAccountKey(),
      jobAttribute: attribute.uuid,
      section: attribute.type,
      suid: attribute.suid,
    };
  }

  private getDeducedAndExtractedData(attributes: QualificationAttribute[]) {
    const displayableExtractedJobseekerQualsSuids = this.getSuidsByInferenceMethod(attributes, [
      InferenceMethod.EXTRACTED,
    ]);
    const displayableDeducedJobseekerQualsSuids = this.getSuidsByInferenceMethod(attributes, [
      InferenceMethod.WEAKLY_INFERRED,
      InferenceMethod.STRONGLY_INFERRED,
    ]);
    return {
      displayableExtractedJobseekerQualsSuids: AvroOptional.string(
        displayableExtractedJobseekerQualsSuids.join(","),
      ),
      displayableExtractedJobseekerQualsSuidCount: AvroOptional.int(
        displayableExtractedJobseekerQualsSuids.length,
      ),
      displayableDeducedJobseekerQualsSuids: AvroOptional.string(
        displayableDeducedJobseekerQualsSuids.join(","),
      ),
      displayableDeducedJobseekerQualsSuidCount: AvroOptional.int(
        displayableDeducedJobseekerQualsSuids.length,
      ),
    };
  }

  private getSuidsByInferenceMethod(
    attributes: QualificationAttribute[],
    inferenceMethods: string[],
  ): string[] {
    return attributes
      ?.filter((req) => inferenceMethods.includes(req.inferenceMethod!))
      .map((req) => req.suid);
  }
}
